/** biome-ignore-all lint/style/useNamingConvention: api expects snake case */
import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import {type FormEventHandler, memo, useCallback, useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {selectEventOptions} from '~/common/fabric/reducers/eviteEvent/selectors';
import type {GuestlistRouteParams} from '~/ViewInvitation/components/Guestlist/GuestlistOverlay';
import {GuestlistOverlayHeader} from '~/ViewInvitation/components/Guestlist/GuestlistOverlayHeader';
import {useGuestApi} from '~/ViewInvitation/components/Guestlist/hooks/useGuestApi';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {DeleteGuestConfirmModal} from '~/ViewInvitation/components/Guestlist/modals/DeleteConfirmModals';
import {type Guest, isHostGuest, type RsvpResponse} from '~/ViewInvitation/types/guests';
import {Button} from '~library/atoms/Button';
import {Counter} from '~library/atoms/Counter';
import {GenericSpinner} from '~library/atoms/GenericSpinner';
import {Input} from '~library/atoms/Input';
import {Select} from '~library/atoms/Select';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';
import {TrashIcon} from '~library/svg/v2/TrashIcon';

import * as styles from './UpdateRSVP.module.scss';

type RsvpOption = {label: string; value: RsvpResponse};

const rsvpOptions: RsvpOption[] = [
  {
    label: 'Yes',
    value: 'yes',
  },
  {
    label: 'Maybe',
    value: 'maybe',
  },
  {
    label: 'No',
    value: 'no',
  },
  {
    label: 'No Reply',
    value: 'undecided',
  },
] as const;

interface UpdateRSVPProps {
  onClose: () => void;
}

// Note: there is commented out code, because hosts don't need to follow limits. However, we might need to
// impose these limits in the future so I am keeping it for now
export const UpdateRSVP = memo(({onClose}: UpdateRSVPProps) => {
  const [animate] = useAutoAnimate();
  const history = useHistory();
  const {userId} = useParams<GuestlistRouteParams>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const eventOptions = useSelector(selectEventOptions, shallowEqual);
  // const maxGuests = eventOptions?.event_option_max_guest_number;
  const allowKids = eventOptions?.event_option_allow_kids;
  const {
    state: {guestlist},
  } = useGuestlistDataContext();

  const guest = guestlist?.find((g: Guest) => g.userId === userId);

  const {updateGuest, isUpdating, isDeleting} = useGuestApi(userId);

  const isUpdatingOrDeleting = isUpdating || isDeleting;

  const selectedRsvpOption = rsvpOptions.find((o) => o.value === guest?.rsvpResponse);

  const [initialFormData] = useState({
    name: guest?.name || '',
    rsvpResponse: selectedRsvpOption || rsvpOptions[3],
    numberOfAdults: guest?.numberOfAdults || 1,
    numberOfKids: guest?.numberOfKids || 0,
  });

  const [formData, setFormData] = useState<{
    name: string;
    rsvpResponse: RsvpOption;
    numberOfAdults?: number;
    numberOfKids?: number;
  }>(initialFormData);

  useEffect(() => {
    if (guest) {
      setFormData({
        name: guest.name || '',
        rsvpResponse: selectedRsvpOption || rsvpOptions[3],
        numberOfAdults: guest.numberOfAdults,
        numberOfKids: guest.numberOfKids,
      });
    }
  }, [guest, selectedRsvpOption]);

  // const totalGuests = (formData?.numberOfAdults ?? 0) + (formData.numberOfKids ?? 0);
  const tooLittleGuests = allowKids && formData.numberOfAdults === 0 && formData.numberOfKids === 0;
  const rsvpResponseNoOrUndecided =
    formData.rsvpResponse.value === 'undecided' || formData.rsvpResponse.value === 'no';
  const formHasntChanged = shallowEqual(formData, initialFormData);
  const disableSaveButton =
    isUpdatingOrDeleting || tooLittleGuests || !formData.name || formHasntChanged;

  const goBack = useCallback(() => {
    history.replace({
      pathname: `/guests/${userId}`,
      search: history.location.search,
    });
  }, [history, userId]);

  const changeFormData = useCallback((name: string, value: string | number | RsvpOption) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const onFormDataChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      changeFormData(e.target.name, e.target.value);
    },
    [changeFormData]
  );

  const onNumberChange = (name: string) => {
    return useCallback(
      (value: number) => {
        changeFormData(name, value);
      },
      [name]
    );
  };

  const onMinus = (name: 'numberOfKids' | 'numberOfAdults') => {
    return () => {
      const curVal = formData[name] || 0;
      const newVal = curVal === 0 ? 0 : curVal - 1;
      changeFormData(name, newVal);
    };
  };

  const onPlus = (name: 'numberOfKids' | 'numberOfAdults') => {
    // const altName = name === 'numberOfKids' ? 'numberOfAdults' : 'numberOfKids';
    return () => {
      // const altVal = formData[altName] || 0;
      const curVal = formData[name] || 0;
      const newVal = curVal === 1000 ? 1000 : curVal + 1;
      // if (newVal + altVal <= maxGuests) {
      changeFormData(name, newVal);
      // }
    };
  };

  const onRsvpChange = useCallback(
    (value: RsvpResponse) => {
      const rsvpResponse = rsvpOptions.find((o) => o.value === value);
      if (rsvpResponse) {
        changeFormData('rsvpResponse', rsvpResponse);
      }
    },
    [changeFormData]
  );

  const onDeleteGuest = useCallback(
    (e?: Error) => {
      if (!e) {
        goBack();
      }
      setDeleteModalOpen(false);
    },
    [goBack]
  );

  const doSave: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isUpdating && !isDeleting) {
        try {
          await updateGuest({
            name: formData.name,
            rsvpResponse: formData.rsvpResponse?.value,
            numberOfAdults: formData.numberOfAdults,
            numberOfKids: formData.numberOfKids,
          });
          toastAlert('Guest successfully updated', {
            icon: <CheckmarkIcon />,
            type: 'success',
          });
          goBack();
        } catch (e) {
          evite.error('Failed to refetch guests', e);
          toastAlert(`Unable to reload guests. Your latest update might not be visible.`, {
            icon: <AlertLineIcon />,
            type: 'error',
          });
        }
      }
    },
    [isUpdating, isDeleting, updateGuest, formData, goBack]
  );

  return (
    <form className={styles.container} onSubmit={doSave}>
      <GuestlistOverlayHeader onClose={onClose} goBack={goBack} />
      <div className={styles.wrapper}>
        <Input
          data-qa-id="guest-name"
          label="Guest Name"
          value={formData.name}
          name="name"
          onChange={onFormDataChange}
          disabled={isUpdatingOrDeleting}
          error={!formData.name ? 'Please enter a name' : undefined}
        />
        <Input
          data-qa-id="guest-contact"
          label="Email / Phone Number"
          value={isHostGuest(guest) ? guest?.phone || guest?.email : ''}
          disabled
        />
        <div
          className={cx({
            'input-wrapper': allowKids,
            [styles.expand]: allowKids,
            [styles.splitInputs]: !allowKids,
          })}
        >
          <div className={cx('input-wrapper')}>
            <Typography variant="label2" as="label" htmlFor="rsvp-response">
              RSVP
            </Typography>
            <Select
              id="rsvp-response"
              placeholder="Change RSVP"
              options={rsvpOptions}
              data-qa-id="rsvp-value-select"
              value={formData.rsvpResponse}
              name="rsvpResponse"
              onChange={onRsvpChange}
              disabled={isUpdatingOrDeleting}
            />
          </div>
          {allowKids || (
            <Counter
              data-qa-id="total-adults"
              as="controls"
              title="Guests"
              count={formData.numberOfAdults || 1}
              name="numberOfAdults"
              inputCallback={onNumberChange('numberOfAdults')}
              min={1}
              max={1000}
              onMinus={onMinus('numberOfAdults')}
              onPlus={onPlus('numberOfAdults')}
              disabled={isUpdatingOrDeleting || rsvpResponseNoOrUndecided}
            />
          )}
        </div>
        {allowKids && (
          <div className={styles.splitInputs}>
            <Counter
              data-qa-id="total-adults"
              as="controls"
              title="Adults"
              count={formData.numberOfAdults || 0}
              name="numberOfAdults"
              inputCallback={onNumberChange('numberOfAdults')}
              min={0}
              max={1000}
              onMinus={onMinus('numberOfAdults')}
              onPlus={onPlus('numberOfAdults')}
              disabled={isUpdatingOrDeleting || rsvpResponseNoOrUndecided}
            />
            <Counter
              data-qa-id="total-kids"
              as="controls"
              title="Kids"
              count={formData.numberOfKids || 0}
              name="numberOfKids"
              inputCallback={onNumberChange('numberOfKids')}
              min={0}
              max={1000}
              onMinus={onMinus('numberOfKids')}
              onPlus={onPlus('numberOfKids')}
              disabled={isUpdatingOrDeleting || rsvpResponseNoOrUndecided}
            />
          </div>
        )}
        <div className={styles.error} ref={animate}>
          {tooLittleGuests && (
            <Typography variant="paragraph3" color="feedbackError">
              You must have at least one guest
            </Typography>
          )}
          {/* Hosts don't have these restrictions, but lets keep this logic */}
          {/*{totalGuests > maxGuests && (*/}
          {/*  <Typography variant="paragraph3" color="feedbackError">*/}
          {/*    The total number of guests exceeds the maximum allowed ({maxGuests})*/}
          {/*  </Typography>*/}
          {/*)}*/}
        </div>
      </div>
      <div className={styles.removeGuest}>
        <Button
          data-qa-id="remove-guest-button"
          variant="link"
          className="update-guests__remove-button"
          onClick={() => setDeleteModalOpen(true)}
          disabled={isUpdatingOrDeleting}
          disableProcessing
        >
          {isDeleting ? <GenericSpinner size={32 * 0.75} /> : <TrashIcon ratio={0.75} />}
          Remove guest
        </Button>
      </div>
      <div className={styles.actionRow}>
        <Button data-qa-id="cancel-button" variant="secondary" size="large" onClick={goBack}>
          Cancel
        </Button>
        <Button
          data-qa-id="save-button"
          variant="primary"
          size="large"
          type="submit"
          disabled={disableSaveButton}
          title={formHasntChanged ? 'No changes to save' : undefined}
          formSubmit
          disableProcessing
        >
          {isUpdating && <GenericSpinner size={16} />}
          Save
        </Button>
      </div>
      {guest && (
        <DeleteGuestConfirmModal
          guest={guest}
          onDelete={onDeleteGuest}
          onCancel={() => setDeleteModalOpen(false)}
          isOpen={deleteModalOpen}
        />
      )}
    </form>
  );
});

UpdateRSVP.displayName = 'UpdateRSVP';
